import { useState, useEffect, useContext, useRef } from "react";
import { SidebarContext } from "../../../shared/context/SidebarContext";
import { fetchVideos, fetchVideoCompradores, fetchVideoEspectadores } from "../services/trackingServiceV2";
import JornadaModal from "../components/JornadaModal";
import { toast } from "react-toastify";
import DateFilterBar from "../components/DateFilterBar";

function formatSeconds(s) {
    if (!s) return "0s";
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m > 0 ? `${m}m${sec}s` : `${sec}s`;
}

function CompradoresModal({ videoLabel, startDate, endDate, filters, onClose }) {
    const [loading, setLoading] = useState(true);
    const [compradores, setCompradores] = useState([]);
    const overlayRef = useRef(null);

    useEffect(() => {
        loadData();
    }, [videoLabel]);

    async function loadData() {
        setLoading(true);
        try {
            const result = await fetchVideoCompradores(startDate, endDate, videoLabel, filters);
            setCompradores(result.compradores || []);
        } catch {
            toast.error("Erro ao buscar compradores");
        }
        setLoading(false);
    }

    function handleOverlayClick(e) {
        if (e.target === overlayRef.current) onClose();
    }

    return (
        <div ref={overlayRef} onClick={handleOverlayClick}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 sm:p-8">
            <div className="bg-surface-1 border border-white/[0.08] rounded-xl w-full max-w-2xl my-4 shadow-2xl">

                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-white/[0.08]">
                    <div>
                        <h2 className="text-lg font-bold text-white">Compradores</h2>
                        <p className="text-xs text-ink-3 truncate max-w-md">{videoLabel}</p>
                    </div>
                    <button onClick={onClose} className="text-ink-3 hover:text-white text-xl leading-none p-1">✕</button>
                </div>

                {loading && (
                    <div className="flex justify-center py-12">
                        <div className="w-8 h-8 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
                    </div>
                )}

                {!loading && compradores.length === 0 && (
                    <div className="p-6 text-center text-ink-4">Nenhum comprador encontrado</div>
                )}

                {!loading && compradores.length > 0 && (
                    <div className="overflow-x-auto max-h-[65vh] overflow-y-auto">
                        <table className="w-full text-sm">
                            <thead className="sticky top-0 bg-surface-2">
                                <tr className="border-b border-white/[0.08] text-ink-3">
                                    <th className="text-left p-3">Nome / Email</th>
                                    <th className="text-left p-3">Tempo Vídeo</th>
                                    <th className="text-left p-3">Retenção</th>
                                    <th className="text-left p-3">Orderbumps</th>
                                    <th className="text-left p-3">Valor Total</th>
                                    <th className="text-left p-3">Produto</th>
                                </tr>
                            </thead>
                            <tbody>
                                {compradores.map((c, i) => {
                                    const retencao = c.video_duration > 0 ? Math.min(((c.video_seconds / c.video_duration) * 100), 100) : 0;
                                    return (
                                        <tr key={i} className="border-b border-white/[0.08] hover:bg-surface-3/30">
                                            <td className="p-3">
                                                <p className="font-medium text-white text-sm">{c.nome || "—"}</p>
                                                <p className="text-xs text-ink-3">{c.email || "—"}</p>
                                            </td>
                                            <td className="p-3">
                                                <span className="text-indigo-400 font-medium">{formatSeconds(c.video_seconds)}</span>
                                                <span className="text-ink-5 text-xs"> / {formatSeconds(c.video_duration)}</span>
                                            </td>
                                            <td className="p-3">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-16 h-2 bg-surface-3 rounded-full overflow-hidden">
                                                        <div className="h-full bg-emerald-500 rounded-full"
                                                            style={{ width: `${retencao}%` }} />
                                                    </div>
                                                    <span className="text-xs text-ink-3">{retencao.toFixed(0)}%</span>
                                                </div>
                                            </td>
                                            <td className="p-3 text-center">
                                                {Number(c.total_orderbumps) > 0 ? (
                                                    <span className="bg-amber-900/40 text-amber-400 px-2 py-0.5 rounded-full text-xs font-medium">
                                                        {c.total_orderbumps}
                                                    </span>
                                                ) : <span className="text-ink-5">0</span>}
                                            </td>
                                            <td className="p-3 text-emerald-400 font-medium text-xs whitespace-nowrap">
                                                R$ {Number(c.valor_total || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                                            </td>
                                            <td className="p-3 text-ink-2 text-xs truncate max-w-[150px]">
                                                {c.nome_produto || "—"}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

// Quem assistiu o vídeo: lead vinculado (último evento lead da pessoa, em qualquer página) quando existe,
// senão "Anônimo" — a jornada abre do mesmo jeito pelo uid.
function EspectadoresModal({ video, startDate, endDate, filters, onClose, onJornada }) {
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [soLeads, setSoLeads] = useState(false);
    const overlayRef = useRef(null);

    useEffect(() => {
        let ativo = true;
        (async () => {
            setLoading(true);
            try {
                const r = await fetchVideoEspectadores(startDate, endDate, video.video_key, filters);
                if (ativo) setRows(r.espectadores || []);
            } catch { toast.error("Erro ao buscar espectadores"); }
            if (ativo) setLoading(false);
        })();
        return () => { ativo = false; };
    }, [video.video_key, startDate, endDate]);

    const lista = soLeads ? rows.filter(r => r.email) : rows;
    const totalLeads = rows.filter(r => r.email).length;

    return (
        <div ref={overlayRef} onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="surface-card w-full max-w-4xl max-h-[85vh] flex flex-col">
                <div className="flex items-start justify-between gap-4 p-5 border-b border-white/[0.06]">
                    <div className="min-w-0">
                        <h2 className="heading-3 truncate" title={video.video_label}>{video.video_label}</h2>
                        <p className="text-xs text-ink-4 mt-0.5">id <code className="font-mono">{video.video_key}</code> · {rows.length} espectadores · {totalLeads} com lead</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <button onClick={() => setSoLeads(v => !v)} className={`text-xs px-3 py-1.5 rounded-lg ring-1 transition-colors ${soLeads ? "bg-brand-500/15 text-brand-200 ring-brand-500/30" : "text-ink-3 ring-white/[0.08] hover:bg-white/[0.05]"}`}>
                            Só com lead
                        </button>
                        <button onClick={onClose} className="text-ink-3 hover:text-ink-1 text-xl leading-none px-1">×</button>
                    </div>
                </div>
                <div className="overflow-auto">
                    {loading ? (
                        <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-brand-400 border-t-transparent rounded-full animate-spin" /></div>
                    ) : lista.length === 0 ? (
                        <p className="text-center text-ink-4 py-10 text-sm">Nenhum espectador{soLeads ? " com lead" : ""} neste vídeo no período.</p>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="sticky top-0 bg-surface-2">
                                <tr className="text-ink-3 border-b border-white/[0.06]">
                                    <th className="text-left p-3">Pessoa</th>
                                    <th className="text-left p-3">Assistiu</th>
                                    <th className="text-left p-3">Retenção</th>
                                    <th className="text-left p-3">Marco</th>
                                    <th className="text-left p-3">Último acesso</th>
                                    <th className="text-left p-3">Comprou</th>
                                    <th className="p-3"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {lista.map((r) => (
                                    <tr key={r.visitor_id} className="border-b border-white/[0.05] hover:bg-white/[0.03]">
                                        <td className="p-3 max-w-[260px]">
                                            {r.email ? (
                                                <>
                                                    <p className="font-medium truncate text-ink-1">{r.nome || "Lead"}</p>
                                                    <p className="text-xs text-ink-3 truncate">{r.email}{r.telefone ? ` · ${r.telefone}` : ""}</p>
                                                </>
                                            ) : (
                                                <>
                                                    <p className="text-ink-3">Anônimo</p>
                                                    <p className="text-[11px] text-ink-5 font-mono truncate">{r.visitor_id}</p>
                                                </>
                                            )}
                                        </td>
                                        <td className="p-3 whitespace-nowrap">{formatSeconds(r.video_seconds)} <span className="text-ink-4">/ {formatSeconds(r.video_duration)}</span></td>
                                        <td className="p-3">
                                            <div className="flex items-center gap-2">
                                                <div className="w-20 h-2 bg-surface-3 rounded-full overflow-hidden">
                                                    <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${Math.min(r.retencao_pct || 0, 100)}%` }} />
                                                </div>
                                                <span className="text-xs text-ink-3">{(r.retencao_pct || 0).toFixed(0)}%</span>
                                            </div>
                                        </td>
                                        <td className="p-3 text-xs text-ink-3">{r.marco >= 25 ? `${r.marco}%` : r.marco === 1 ? "início" : "—"}</td>
                                        <td className="p-3 text-xs text-ink-3 whitespace-nowrap">{r.ultimo ? new Date(r.ultimo).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                                        <td className="p-3">{r.comprou ? <span className="chip-positive">Sim</span> : <span className="text-ink-4">—</span>}</td>
                                        <td className="p-3 text-right">
                                            <button onClick={() => onJornada(r.visitor_id)} className="text-xs text-brand-300 hover:text-brand-200 underline underline-offset-2 whitespace-nowrap">Jornada</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function VideoAnalytics() {
    const { isOpen } = useContext(SidebarContext);

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [modalVideo, setModalVideo] = useState(null);
    const [modalEspectadores, setModalEspectadores] = useState(null);
    const [jornadaVisitor, setJornadaVisitor] = useState(null);
    const [lastDates, setLastDates] = useState(null);

    async function handleBuscar(startDate, endDate, filters = {}) {
        setLoading(true);
        setLastDates({ startDate, endDate, filters });
        try {
            const result = await fetchVideos(startDate, endDate, filters);
            setData(result);
        } catch {
            toast.error("Erro ao buscar vídeos");
        }
        setLoading(false);
    }

    return (
        <div className="min-h-screen bg-surface-1 text-white flex">
            <main className={`transition-all duration-300 ${isOpen ? "lg:ml-64" : "sm:ml-16"} w-full p-6 mb-16 sm:mb-0`}>

                <header className="mb-6">
                    <h1 className="text-3xl font-bold mb-1">Analytics de Vídeos</h1>
                    <p className="text-sm text-ink-3 mb-4">Vídeos assistidos nas páginas do produto selecionado e quem comprou depois.</p>
                    <DateFilterBar onBuscar={handleBuscar} loading={loading} />
                </header>

                {!data && !loading && (
                    <p className="text-center text-ink-4 py-8">Selecione um período e clique em Buscar</p>
                )}

                {loading && (
                    <div className="flex justify-center py-12">
                        <div className="w-8 h-8 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
                    </div>
                )}

                {data && !loading && (
                    <div className="space-y-6">
                        {/* KPIs */}
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                            {[
                                { label: "Visitantes do produto", value: data.kpis?.visitantes ?? 0, color: "text-blue-400" },
                                { label: "Vídeos Rastreados", value: data.kpis?.videos_rastreados ?? 0, color: "text-indigo-400" },
                                { label: "Assistiram vídeo", value: data.kpis?.total_espectadores ?? 0, color: "text-cyan-400" },
                                { label: "Tempo Médio (s)", value: data.kpis?.media_segundos_assistidos ?? 0, color: "text-yellow-400" },
                                { label: "Compraram após vídeo", value: data.kpis?.compraram_apos_video ?? 0, color: "text-emerald-400" },
                            ].map(kpi => (
                                <div key={kpi.label} className="bg-surface-2 p-4 rounded-lg">
                                    <p className="text-xs text-ink-3">{kpi.label}</p>
                                    <p className={`text-2xl font-bold ${kpi.color}`}>{kpi.value}</p>
                                </div>
                            ))}
                        </div>

                        {/* Per Video Table */}
                        <div className="bg-surface-2 rounded-lg overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-white/[0.08] text-ink-3">
                                        <th className="text-left p-3">Vídeo</th>
                                        <th className="text-left p-3">Espectadores</th>
                                        <th className="text-left p-3">Leads</th>
                                        <th className="text-left p-3">Tempo Médio</th>
                                        <th className="text-left p-3">Duração</th>
                                        <th className="text-left p-3">Retenção</th>
                                        <th className="text-left p-3">Compradores</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.videos && data.videos.map((v, i) => (
                                        <tr key={i} className="border-b border-white/[0.08] hover:bg-surface-3/30">
                                            <td className="p-3 font-medium max-w-[250px]">
                                                <p className="truncate" title={v.video_label}>{v.video_label}</p>
                                                {v.video_key !== v.video_label && <p className="text-[11px] text-ink-5 font-mono truncate">{v.video_key}</p>}
                                            </td>
                                            <td className="p-3">
                                                <button onClick={() => setModalEspectadores(v)} className="hover:text-brand-200 underline underline-offset-2 decoration-white/20">{v.espectadores}</button>
                                            </td>
                                            <td className="p-3">
                                                {v.leads > 0 ? (
                                                    <button onClick={() => setModalEspectadores(v)}
                                                        className="bg-brand-500/15 text-brand-200 hover:bg-brand-500/25 px-2.5 py-1 rounded-full text-xs font-medium transition-colors">
                                                        {v.leads} 👤
                                                    </button>
                                                ) : <span className="text-ink-4">—</span>}
                                            </td>
                                            <td className="p-3">{formatSeconds(v.media_segundos)}</td>
                                            <td className="p-3">{formatSeconds(v.duracao_total)}</td>
                                            <td className="p-3">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-20 h-2 bg-surface-3 rounded-full overflow-hidden">
                                                        <div className="h-full bg-emerald-500 rounded-full"
                                                            style={{ width: `${Math.min(v.retencao_pct || 0, 100)}%` }} />
                                                    </div>
                                                    <span className="text-xs text-ink-3">{(v.retencao_pct || 0).toFixed(0)}%</span>
                                                </div>
                                            </td>
                                            <td className="p-3">
                                                {v.compradores > 0 ? (
                                                    <button
                                                        onClick={() => setModalVideo(v)}
                                                        className="bg-emerald-900/50 text-emerald-400 hover:bg-emerald-800/60 px-2.5 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer">
                                                        {v.compradores} 👁️
                                                    </button>
                                                ) : "—"}
                                            </td>
                                        </tr>
                                    ))}
                                    {(!data.videos || data.videos.length === 0) && (
                                        <tr><td colSpan={7} className="text-center text-ink-4 p-6">
                                            Nenhum vídeo rastreado nas páginas deste produto no período.
                                            <span className="block text-xs text-ink-5 mt-1">O tracker registra progresso de vídeo HTML5 e YouTube a cada 10 s; páginas sem player ou com player em iframe de terceiro não geram evento.</span>
                                        </td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

            </main>

            {/* Buyers Modal */}
            {modalEspectadores && lastDates && (
                <EspectadoresModal
                    video={modalEspectadores}
                    startDate={lastDates.startDate}
                    endDate={lastDates.endDate}
                    filters={lastDates.filters}
                    onClose={() => setModalEspectadores(null)}
                    onJornada={(vid) => setJornadaVisitor(vid)}
                />
            )}
            {jornadaVisitor && <JornadaModal visitorId={jornadaVisitor} onClose={() => setJornadaVisitor(null)} />}
            {modalVideo && lastDates && (
                <CompradoresModal
                    videoLabel={modalVideo.video_key}
                    startDate={lastDates.startDate}
                    endDate={lastDates.endDate}
                    filters={lastDates.filters}
                    onClose={() => setModalVideo(null)}
                />
            )}
        </div>
    );
}
